import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Drawer,
  Button,
  Tag,
  Typography,
  Space,
  List,
  Tooltip,
  Select,
  Switch,
} from 'antd';
import {
  ExclamationCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  CheckOutlined,
  SearchOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { createApiClient } from '@/services/apiClient';
import './AnomalyAlertOverlay.css';

const { Text, Paragraph } = Typography;
const EMPTY_ALERTS: AnomalyAlert[] = [];

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AnomalyAlert {
  id: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  category: 'fraud' | 'compliance' | 'operational' | 'risk' | 'system';
  title: string;
  description: string;
  entityType?: string;
  entityId?: string;
  entityName?: string;
  detectedAt: string;
  expiresAt?: string;
  actionRequired: boolean;
  suggestedAction?: string;
  acknowledged: boolean;
}

interface AlertFilters {
  severity?: string;
  category?: string;
  acknowledged?: boolean;
}

// ─── API Client ──────────────────────────────────────────────────────────────

const apiClient = createApiClient(import.meta.env.VITE_GATEWAY_URL || '/api');

// ─── Hooks ───────────────────────────────────────────────────────────────────

export const useAnomalyAlerts = (filters?: AlertFilters) =>
  useQuery<AnomalyAlert[]>({
    queryKey: ['anomaly-alerts', filters],
    queryFn: () =>
      apiClient
        .get('/api/ai/anomaly-alerts', { params: filters })
        .then((r) => r.data)
        .catch((err: unknown) => {
          const status = (err as { response?: { status?: number } })?.response?.status;
          if (status === 404) return EMPTY_ALERTS;
          throw err;
        }),
    refetchInterval: 30000,
    retry: false,
  });

export const useAcknowledgeAlert = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient
        .post(`/api/ai/anomaly-alerts/${id}/acknowledge`)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['anomaly-alerts'] }),
  });
};

export const useSnoozeAlert = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, duration }: { id: string; duration: number }) =>
      apiClient
        .post(`/api/ai/anomaly-alerts/${id}/snooze`, { duration })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['anomaly-alerts'] }),
  });
};

export const useUnacknowledgedCount = () =>
  useQuery<number>({
    queryKey: ['anomaly-alerts-count'],
    queryFn: () =>
      apiClient
        .get('/api/ai/anomaly-alerts/count', {
          params: { acknowledged: false },
        })
        .then((r) => r.data)
        .catch((err: unknown) => {
          const status = (err as { response?: { status?: number } })?.response?.status;
          if (status === 404) return 0;
          throw err;
        }),
    refetchInterval: 30000,
    retry: false,
  });

// ─── Helpers ─────────────────────────────────────────────────────────────────

const SEVERITY_CONFIG = {
  critical: { color: '#ff4d4f', icon: <ExclamationCircleOutlined />, tag: 'red' },
  high: { color: '#fa8c16', icon: <WarningOutlined />, tag: 'orange' },
  medium: { color: '#faad14', icon: <InfoCircleOutlined />, tag: 'gold' },
  low: { color: '#1890ff', icon: <InfoCircleOutlined />, tag: 'blue' },
} as const;

const CATEGORY_COLORS: Record<string, string> = {
  fraud: 'volcano',
  compliance: 'purple',
  operational: 'cyan',
  risk: 'orange',
  system: 'geekblue',
};

function timeAgo(dateStr: string): string {
  const seconds = Math.floor(
    (Date.now() - new Date(dateStr).getTime()) / 1000,
  );
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function getEntityRoute(type?: string, id?: string): string | null {
  if (!type || !id) return null;
  switch (type) {
    case 'customer':
      return `/customers/${id}`;
    case 'account':
      return `/loans/accounts/${id}`;
    case 'transaction':
      return `/payment-ops/transactions/${id}`;
    default:
      return `/search?entity=${id}`;
  }
}

const SOUND_KEY = 'anomaly_alerts_sound';

// ─── Component ───────────────────────────────────────────────────────────────

export const AnomalyAlertOverlay: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [drawerFilters, setDrawerFilters] = useState<AlertFilters>({});
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try {
      return localStorage.getItem(SOUND_KEY) !== 'false';
    } catch {
      return true;
    }
  });
  const [prevAlertIds, setPrevAlertIds] = useState<Set<string>>(new Set());

  const navigate = useNavigate();

  // Listen for TopBar trigger to open the drawer
  useEffect(() => {
    const handleOpen = () => setDrawerOpen(true);
    window.addEventListener('toggleAnomalyDrawer', handleOpen);
    return () => window.removeEventListener('toggleAnomalyDrawer', handleOpen);
  }, []);

  const { data: alerts = EMPTY_ALERTS } = useAnomalyAlerts({ acknowledged: false });
  const { data: allAlerts = EMPTY_ALERTS } = useAnomalyAlerts(drawerFilters);
  const { data: unackCount = 0 } = useUnacknowledgedCount();
  const acknowledgeMut = useAcknowledgeAlert();
  const snoozeMut = useSnoozeAlert();

  const visibleAlerts = useMemo(
    () => [...alerts].sort((a, b) =>
      new Date(b.detectedAt).getTime() - new Date(a.detectedAt).getTime(),
    ).slice(0, 5),
    [alerts],
  );

  // Sound notification for new critical alerts
  useEffect(() => {
    if (!soundEnabled) return;
    const currentIds = new Set(alerts.map((a) => a.id));
    const newCritical = alerts.filter(
      (a) => a.severity === 'critical' && !prevAlertIds.has(a.id),
    );
    if (newCritical.length > 0 && prevAlertIds.size > 0) {
      try {
        const ctx = new AudioContext();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 880;
        gain.gain.value = 0.1;
        osc.start();
        osc.stop(ctx.currentTime + 0.2);
      } catch {
        /* audio not available */
      }
    }
    setPrevAlertIds(currentIds);
  }, [alerts, soundEnabled]);

  // Esc dismisses top alert
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && visibleAlerts.length > 0 && !drawerOpen) {
        acknowledgeMut.mutate(visibleAlerts[0].id);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [visibleAlerts, drawerOpen, acknowledgeMut]);

  const handleSoundToggle = useCallback((checked: boolean) => {
    setSoundEnabled(checked);
    try {
      localStorage.setItem(SOUND_KEY, String(checked));
    } catch {
      /* noop */
    }
  }, []);

  const handleInvestigate = useCallback(
    (alert: AnomalyAlert) => {
      const route = getEntityRoute(alert.entityType, alert.entityId);
      if (route) navigate(route);
    },
    [navigate],
  );

  const handleSnooze = useCallback(
    (id: string) => snoozeMut.mutate({ id, duration: 3600 }),
    [snoozeMut],
  );

  const toggleExpand = useCallback(
    (id: string) => setExpandedId((prev) => (prev === id ? null : id)),
    [],
  );

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <>
      {/* Toast notification stack */}
      <div className="anomaly-alert-stack">
        <AnimatePresence initial={false}>
          {visibleAlerts.map((alert) => {
            const sev = SEVERITY_CONFIG[alert.severity];
            const isExpanded = expandedId === alert.id;

            return (
              <motion.div
                key={alert.id}
                layout
                initial={{ opacity: 0, x: 320, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 320, scale: 0.95 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className={`anomaly-alert-card anomaly-severity-${alert.severity}`}
                onClick={() => toggleExpand(alert.id)}
                role="alert"
              >
                <div
                  className="anomaly-alert-severity-strip"
                  style={{ backgroundColor: sev.color }}
                />
                <div className="anomaly-alert-body">
                  <div className="anomaly-alert-header">
                    <span className="anomaly-alert-icon" style={{ color: sev.color }}>
                      {sev.icon}
                    </span>
                    <Text strong ellipsis className="anomaly-alert-title">
                      {alert.title}
                    </Text>
                    <Text type="secondary" className="anomaly-alert-time">
                      {timeAgo(alert.detectedAt)}
                    </Text>
                  </div>

                  <Paragraph
                    ellipsis={isExpanded ? false : { rows: 1 }}
                    className="anomaly-alert-desc"
                  >
                    {alert.description}
                  </Paragraph>

                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="anomaly-alert-detail"
                    >
                      {alert.entityName && (
                        <Text type="secondary">
                          Entity: <Text strong>{alert.entityName}</Text>
                        </Text>
                      )}
                      {alert.suggestedAction && (
                        <Text type="secondary" className="anomaly-suggested-action">
                          Suggested: {alert.suggestedAction}
                        </Text>
                      )}
                      <Space className="anomaly-alert-tags">
                        <Tag color={sev.tag}>{alert.severity}</Tag>
                        <Tag color={CATEGORY_COLORS[alert.category]}>
                          {alert.category}
                        </Tag>
                        {alert.actionRequired && (
                          <Tag color="red">Action Required</Tag>
                        )}
                      </Space>
                    </motion.div>
                  )}

                  <Space
                    className="anomaly-alert-actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Tooltip title="Acknowledge">
                      <Button
                        size="small"
                        type="text"
                        icon={<CheckOutlined />}
                        onClick={() => acknowledgeMut.mutate(alert.id)}
                        loading={acknowledgeMut.isPending}
                      />
                    </Tooltip>
                    {alert.entityId && (
                      <Tooltip title="Investigate">
                        <Button
                          size="small"
                          type="text"
                          icon={<SearchOutlined />}
                          onClick={() => handleInvestigate(alert)}
                        />
                      </Tooltip>
                    )}
                    <Tooltip title="Snooze 1h">
                      <Button
                        size="small"
                        type="text"
                        icon={<ClockCircleOutlined />}
                        onClick={() => handleSnooze(alert.id)}
                        loading={snoozeMut.isPending}
                      />
                    </Tooltip>
                  </Space>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Full alert drawer */}
      <Drawer
        title="Anomaly Alerts"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={480}
        extra={
          <Space>
            <Text type="secondary">Sound</Text>
            <Switch
              size="small"
              checked={soundEnabled}
              onChange={handleSoundToggle}
            />
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
          <Space wrap>
            <Select
              placeholder="Severity"
              allowClear
              style={{ width: 120 }}
              onChange={(v) =>
                setDrawerFilters((f) => ({ ...f, severity: v }))
              }
              options={[
                { label: 'Critical', value: 'critical' },
                { label: 'High', value: 'high' },
                { label: 'Medium', value: 'medium' },
                { label: 'Low', value: 'low' },
              ]}
            />
            <Select
              placeholder="Category"
              allowClear
              style={{ width: 130 }}
              onChange={(v) =>
                setDrawerFilters((f) => ({ ...f, category: v }))
              }
              options={[
                { label: 'Fraud', value: 'fraud' },
                { label: 'Compliance', value: 'compliance' },
                { label: 'Operational', value: 'operational' },
                { label: 'Risk', value: 'risk' },
                { label: 'System', value: 'system' },
              ]}
            />
            <Select
              placeholder="Status"
              allowClear
              style={{ width: 140 }}
              onChange={(v) =>
                setDrawerFilters((f) => ({
                  ...f,
                  acknowledged: v === undefined ? undefined : v === 'true',
                }))
              }
              options={[
                { label: 'Unacknowledged', value: 'false' },
                { label: 'Acknowledged', value: 'true' },
              ]}
            />
          </Space>
        </Space>

        <List
          dataSource={allAlerts}
          locale={{ emptyText: 'No alerts match the selected filters' }}
          renderItem={(alert) => {
            const sev = SEVERITY_CONFIG[alert.severity];
            return (
              <List.Item
                className={`anomaly-drawer-item${alert.acknowledged ? ' anomaly-acknowledged' : ''}`}
                actions={[
                  !alert.acknowledged && (
                    <Tooltip title="Acknowledge" key="ack">
                      <Button
                        size="small"
                        type="text"
                        icon={<CheckOutlined />}
                        onClick={() => acknowledgeMut.mutate(alert.id)}
                      />
                    </Tooltip>
                  ),
                  alert.entityId && (
                    <Tooltip title="Investigate" key="inv">
                      <Button
                        size="small"
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={() => handleInvestigate(alert)}
                      />
                    </Tooltip>
                  ),
                  !alert.acknowledged && (
                    <Tooltip title="Snooze 1h" key="snz">
                      <Button
                        size="small"
                        type="text"
                        icon={<ClockCircleOutlined />}
                        onClick={() => handleSnooze(alert.id)}
                      />
                    </Tooltip>
                  ),
                ].filter(Boolean)}
              >
                <List.Item.Meta
                  avatar={
                    <span style={{ color: sev.color, fontSize: 18 }}>
                      {sev.icon}
                    </span>
                  }
                  title={
                    <Space>
                      <Text
                        strong
                        delete={alert.acknowledged}
                      >
                        {alert.title}
                      </Text>
                      <Tag color={sev.tag} style={{ marginRight: 0 }}>
                        {alert.severity}
                      </Tag>
                    </Space>
                  }
                  description={
                    <>
                      <Paragraph
                        ellipsis={{ rows: 2 }}
                        style={{ marginBottom: 4 }}
                      >
                        {alert.description}
                      </Paragraph>
                      <Space size={4}>
                        <Tag color={CATEGORY_COLORS[alert.category]}>
                          {alert.category}
                        </Tag>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {timeAgo(alert.detectedAt)}
                        </Text>
                        {alert.entityName && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            · {alert.entityName}
                          </Text>
                        )}
                      </Space>
                    </>
                  }
                />
              </List.Item>
            );
          }}
        />
      </Drawer>
    </>
  );
};
